// services/eventDateService.js
import api from '../../../api/axios';

class EventDateService {
  /**
   * Obtiene todos los eventos destacados
   */
  static async getAllFeaturedEvents() {
    try {
      const response = await api.get('/events/featured');
      
      return response.data;
    } catch (error) {
      console.error('Error fetching featured events:', error);
      return [];
    }
  }

  /**
   * Obtiene un evento destacado por slug
   */
  static async getFeaturedEventBySlug(slug) {
    try {
      const events = await this.getAllFeaturedEvents();
      // Buscar por slug en el array
      const found = events.find(event => event.slug === slug);
      
      
      return found || null;
    } catch (error) {
      console.error(`Error fetching featured event for slug ${slug}:`, error);
      return null;
    }
  }

  /**
   * Obtiene la fecha de un evento destacado
   * 🔥 Busca en todos los posibles nombres de campo
   */
  static getEventDateFromObject(event) {
    if (!event) return null;
    
    // Buscar en diferentes formatos de fecha
    return (
      event.event_date ||        // ✅ Formato con guión bajo
      event.eventDate ||         // ✅ Formato camelCase
      event.end_date ||          // ✅ Formato original de BD
      event.endDate ||           // ✅ Formato camelCase
      null
    );
  }

  /**
   * Obtiene la fecha del evento por slug
   */
  static async getEventEndDate(slug) {
    try {
      const featured = await this.getFeaturedEventBySlug(slug);
      
      if (!featured) {
        
        return null;
      }
      
      // 🔥 Usar el helper para buscar la fecha
      const endDate = this.getEventDateFromObject(featured);
 
      
      // Si el evento está cancelado o TBD, no tiene fecha válida
      if (featured.event_status === 'cancelled' || featured.event_status === 'tbd') {
        return null;
      }
      
      return endDate;
    } catch (error) {
      console.error(`Error getting end date for ${slug}:`, error);
      return null;
    }
  }

  /**
   * Calcula la cuenta regresiva
   */
  static calculateCountdown(endDate) {
    if (!endDate) {
      return { 
        noDate: true,
        expired: false,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      };
    }
    
    const target = new Date(endDate);
    const now = new Date();
    const diff = target - now;
    
    if (diff <= 0) {
      return { 
        expired: true,
        noDate: false,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      };
    }
    
    return {
      expired: false,
      noDate: false,
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60)
    };
  }

  /**
   * Obtiene la cuenta regresiva completa en un solo paso
   */
  static async getCountdown(slug) {
    try {
      const endDate = await this.getEventEndDate(slug);
      return this.calculateCountdown(endDate);
    } catch (error) {
      console.error(`Error getting countdown for ${slug}:`, error);
      return {
        error: true,
        noDate: true,
        expired: false,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      };
    }
  }

  /**
   * Obtiene todos los datos del evento destacado
   */
  static async getFeaturedEventData(slug) {
    try {
      const featured = await this.getFeaturedEventBySlug(slug);
      
      if (!featured) {
        
        return null;
      }
      
     
      
      // 🔥 Obtener la fecha con el helper
      const endDate = this.getEventDateFromObject(featured);
      
      return {
        endDate: endDate,
        eventStatus: featured.event_status || featured.eventStatus || 'scheduled',
        statusMessage: featured.status_message || featured.statusMessage || '',
        locationName: featured.location_name || featured.locationName || null,
        locationAddress: featured.location_address || featured.locationAddress || null,
        locationCity: featured.location_city || featured.locationCity || null,
        locationCountry: featured.location_country || featured.locationCountry || 'Bolivia',
        locationMapUrl: featured.location_map_url || featured.locationMapUrl || null,
        order: featured.featured_order || featured.order || 0,
        scheduleData: featured.schedule_data || null,
        hasDate: !!endDate,
        isTBD: (featured.event_status || featured.eventStatus) === 'tbd',
        isCancelled: (featured.event_status || featured.eventStatus) === 'cancelled',
        isRescheduled: (featured.event_status || featured.eventStatus) === 'rescheduled',
        isScheduled: (featured.event_status || featured.eventStatus) === 'scheduled'
      };
    } catch (error) {
      console.error(`Error getting featured event data for ${slug}:`, error);
      return null;
    }
  }

  /**
   * Obtiene el mensaje de estado para mostrar al usuario
   */
  static async getStatusDisplay(slug) {
    try {
      const featured = await this.getFeaturedEventBySlug(slug);
      if (!featured) return null;
      
      const statusMap = {
        'scheduled': {
          icon: '📅',
          message: 'Fecha confirmada',
          className: 'status-scheduled'
        },
        'rescheduled': {
          icon: '🔄',
          message: featured.status_message || featured.statusMessage || 'Evento reprogramado',
          className: 'status-rescheduled'
        },
        'cancelled': {
          icon: '❌',
          message: featured.status_message || featured.statusMessage || 'Evento cancelado',
          className: 'status-cancelled'
        },
        'tbd': {
          icon: '⏰',
          message: featured.status_message || featured.statusMessage || 'Fecha por confirmar',
          className: 'status-tbd'
        }
      };
      
      return statusMap[featured.event_status || featured.eventStatus] || null;
    } catch (error) {
      console.error(`Error getting status display for ${slug}:`, error);
      return null;
    }
  }
}

export default EventDateService;